/**
 * RettVerk 1.1 - Local Storage Service (Privacy-First v2.0)
 * Håndterer lokal lagring av dokumenter og søkehistorikk
 * Sist oppdatert: 20. februar 2026 — versjon 2.0.0
 * 
 * PERSONVERNPRINSIPP:
 * - Dokumentinnhold lagres KUN lokalt på brukerens enhet
 * - Søketekster lagres KUN lokalt
 * - Backend lagrer kun metadata
 * - INGEN automatisk fallback til server
 * - Full kryptering med AES-256-GCM via encryptionService
 */

import {
  encryptText,
  decryptText,
  encryptJSON,
  decryptJSON,
  isKeyUnlocked,
  isEncryptionSetup
} from './encryptionService';

const DB_NAME = 'RettVerkLocalDB';
const DB_VERSION = 3; // Versjon 3: Integrert med encryptionService

// Store names
const STORES = {
  DOCUMENTS: 'documents',
  SEARCH_HISTORY: 'searchHistory',
  SETTINGS: 'settings',
  CHAT_HISTORY: 'chatHistory'
};

// Local availability status
export const LOCAL_STATUS = {
  AVAILABLE: 'available',           // Grønn - tilgjengelig lokalt
  NOT_ON_DEVICE: 'not_on_device',   // Gul - ikke på denne enheten
  MISSING: 'missing'                // Rød - mangler/flyttet/slettet
};

let dbInstance = null;
let storageAvailable = null;

/**
 * Check if local storage (IndexedDB) is available
 */
export const checkStorageAvailability = async () => {
  if (storageAvailable !== null) {
    return storageAvailable;
  }

  try {
    // Test IndexedDB availability
    if (!window.indexedDB) {
      storageAvailable = false;
      return false;
    }

    // Try to open a test database
    const testRequest = indexedDB.open('_test_availability', 1);
    
    await new Promise((resolve, reject) => {
      testRequest.onerror = () => {
        storageAvailable = false;
        reject(new Error('IndexedDB not available'));
      };
      testRequest.onsuccess = () => {
        // Clean up test database
        testRequest.result.close();
        indexedDB.deleteDatabase('_test_availability');
        storageAvailable = true;
        resolve(true);
      };
    });

    return storageAvailable;
  } catch (error) {
    console.warn('Local storage not available:', error);
    storageAvailable = false;
    return false;
  }
};

/**
 * Get storage availability status with message
 */
export const getStorageStatus = async () => {
  const available = await checkStorageAvailability();
  
  if (available) {
    return {
      available: true,
      message: null
    };
  }
  
  return {
    available: false,
    message: 'RettVerk kan ikke lagre dokumenter og søk lokalt på denne enheten. Du kan fortsatt generere dokumenter, men de vil ikke vises i dokumentbiblioteket eller søkehistorikken.'
  };
};

/**
 * Initialize IndexedDB with version migration support
 */
const initDB = () => {
  return new Promise(async (resolve, reject) => {
    // Check availability first
    const available = await checkStorageAvailability();
    if (!available) {
      reject(new Error('STORAGE_UNAVAILABLE'));
      return;
    }

    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      const oldVersion = event.oldVersion;

      // Log version upgrade for debugging
      // console.log(`Upgrading IndexedDB from v${oldVersion} to v${DB_VERSION}`);

      // Version 1: Initial schema
      if (oldVersion < 1) {
        // Documents store
        if (!db.objectStoreNames.contains(STORES.DOCUMENTS)) {
          const docStore = db.createObjectStore(STORES.DOCUMENTS, { keyPath: 'id' });
          docStore.createIndex('created_at', 'created_at', { unique: false });
          docStore.createIndex('doc_type', 'doc_type', { unique: false });
          docStore.createIndex('status', 'status', { unique: false });
          docStore.createIndex('local_status', 'local_status', { unique: false });
        }

        // Search history store
        if (!db.objectStoreNames.contains(STORES.SEARCH_HISTORY)) {
          const searchStore = db.createObjectStore(STORES.SEARCH_HISTORY, { keyPath: 'id', autoIncrement: true });
          searchStore.createIndex('timestamp', 'timestamp', { unique: false });
          searchStore.createIndex('module', 'module', { unique: false });
        }

        // Settings store
        if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
          db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
        }
      }

      // Version 2: Add encryption keys store and local_status
      if (oldVersion < 2) {
        // Note: Existing documents will be migrated with local_status in a post-upgrade step
      }

      // Version 3: Add chat history store (encryption now handled by encryptionService)
      if (oldVersion < 3) {
        if (!db.objectStoreNames.contains(STORES.CHAT_HISTORY)) {
          const chatStore = db.createObjectStore(STORES.CHAT_HISTORY, { keyPath: 'id' });
          chatStore.createIndex('conversation_id', 'conversation_id', { unique: false });
          chatStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      }
    };
  });
};

// ═══════════════════════════════════════════════════════════════
// ENCRYPTION WRAPPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Encrypt content using encryptionService (if key is unlocked)
 */
const encryptContent = async (content) => {
  if (!content) return null;
  
  try {
    // Check if encryption is available
    const setup = await isEncryptionSetup();
    if (!setup || !isKeyUnlocked()) {
      // Return unencrypted if encryption not set up or locked
      return content;
    }
    
    return await encryptText(content);
  } catch (error) {
    console.warn('Encryption not available, storing unencrypted');
    return content;
  }
};

/**
 * Decrypt content using encryptionService
 */
const decryptContent = async (encryptedContent, wasEncrypted = true) => {
  if (!encryptedContent) return null;
  
  try {
    // If not marked as encrypted, return as-is
    if (!wasEncrypted) return encryptedContent;
    
    // Check if encryption key is available
    if (!isKeyUnlocked()) {
      throw new Error('Krypteringsnøkkel er låst');
    }
    
    return await decryptText(encryptedContent);
  } catch (error) {
    // Return as-is for unencrypted legacy content
    console.warn('Decryption failed, returning as-is');
    return encryptedContent;
  }
};

// ═══════════════════════════════════════════════════════════════
// DOCUMENT OPERATIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Save document content locally (with encryption)
 * @param {string} id - Document ID (from backend metadata)
 * @param {object} document - Document data including content
 */
export const saveDocumentLocally = async (id, document) => {
  const status = await getStorageStatus();
  if (!status.available) {
    throw new Error('STORAGE_UNAVAILABLE');
  }

  const db = await initDB();
  
  // Encrypt content before storing
  const encryptedContent = await encryptContent(document.content);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.DOCUMENTS], 'readwrite');
    const store = transaction.objectStore(STORES.DOCUMENTS);
    
    const docData = {
      id,
      content: encryptedContent,
      content_encrypted: true,
      title: document.title,
      doc_type: document.doc_type,
      status: document.status || 'utkast',
      local_status: LOCAL_STATUS.AVAILABLE,
      created_at: document.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      local_saved_at: new Date().toISOString(),
      content_hash: generateHash(document.content)
    };

    const request = store.put(docData);
    
    request.onsuccess = () => resolve({ ...docData, content: document.content }); // Return unencrypted
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get document content from local storage (with decryption)
 * @param {string} id - Document ID
 */
export const getDocumentLocally = async (id) => {
  const status = await getStorageStatus();
  if (!status.available) {
    return null;
  }

  const db = await initDB();
  return new Promise(async (resolve, reject) => {
    const transaction = db.transaction([STORES.DOCUMENTS], 'readonly');
    const store = transaction.objectStore(STORES.DOCUMENTS);
    const request = store.get(id);
    
    request.onsuccess = async () => {
      if (!request.result) {
        resolve(null);
        return;
      }
      
      const doc = request.result;
      
      // Decrypt content if encrypted
      if (doc.content_encrypted && doc.content) {
        doc.content = await decryptContent(doc.content);
      }
      
      resolve(doc);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all locally stored documents
 */
export const getAllLocalDocuments = async () => {
  const status = await getStorageStatus();
  if (!status.available) {
    return [];
  }

  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.DOCUMENTS], 'readonly');
    const store = transaction.objectStore(STORES.DOCUMENTS);
    const request = store.getAll();
    
    request.onsuccess = async () => {
      const docs = request.result || [];
      
      // Decrypt all documents (but don't include full content in list for performance)
      const processedDocs = docs.map(doc => ({
        ...doc,
        content: undefined, // Don't include content in list
        has_local_content: !!doc.content
      }));
      
      resolve(processedDocs);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Update document content locally
 * @param {string} id - Document ID
 * @param {object} updates - Fields to update
 */
export const updateDocumentLocally = async (id, updates) => {
  const existing = await getDocumentLocally(id);
  if (!existing) {
    throw new Error('Document not found locally');
  }
  
  const updated = {
    ...existing,
    ...updates,
    updated_at: new Date().toISOString()
  };
  
  if (updates.content) {
    updated.content_hash = generateHash(updates.content);
  }
  
  return saveDocumentLocally(id, updated);
};

/**
 * Delete document from local storage
 * @param {string} id - Document ID
 * @param {boolean} confirmed - User has explicitly confirmed deletion
 */
export const deleteDocumentLocally = async (id, confirmed = false) => {
  if (!confirmed) {
    throw new Error('DELETION_NOT_CONFIRMED');
  }

  const status = await getStorageStatus();
  if (!status.available) {
    return true; // Nothing to delete
  }

  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.DOCUMENTS], 'readwrite');
    const store = transaction.objectStore(STORES.DOCUMENTS);
    const request = store.delete(id);
    
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Check if document exists locally and get its status
 * @param {string} id - Document ID
 */
export const getDocumentLocalStatus = async (id) => {
  const status = await getStorageStatus();
  if (!status.available) {
    return LOCAL_STATUS.NOT_ON_DEVICE;
  }

  const doc = await getDocumentLocally(id);
  
  if (!doc) {
    return LOCAL_STATUS.NOT_ON_DEVICE;
  }
  
  if (!doc.content && !doc.has_local_content) {
    return LOCAL_STATUS.MISSING;
  }
  
  return LOCAL_STATUS.AVAILABLE;
};

/**
 * Check if document exists locally
 * @param {string} id - Document ID
 */
export const documentExistsLocally = async (id) => {
  const localStatus = await getDocumentLocalStatus(id);
  return localStatus === LOCAL_STATUS.AVAILABLE;
};

/**
 * Mark document as available on this device (for import)
 */
export const markDocumentAsAvailable = async (id, content) => {
  const existing = await getDocumentLocally(id);
  
  if (existing) {
    return updateDocumentLocally(id, { 
      content, 
      local_status: LOCAL_STATUS.AVAILABLE 
    });
  }
  
  // Create new local entry
  return saveDocumentLocally(id, {
    content,
    title: 'Importert dokument',
    doc_type: 'annet',
    status: 'ferdig'
  });
};

// ═══════════════════════════════════════════════════════════════
// SEARCH HISTORY OPERATIONS (with encryption)
// ═══════════════════════════════════════════════════════════════

/**
 * Save search query locally (encrypted)
 * @param {object} search - Search data
 */
export const saveSearchLocally = async (search) => {
  const status = await getStorageStatus();
  if (!status.available) {
    console.warn('Cannot save search: local storage unavailable');
    return null;
  }

  const db = await initDB();
  
  // Encrypt the search query
  const encryptedQuery = await encryptContent(search.query);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SEARCH_HISTORY], 'readwrite');
    const store = transaction.objectStore(STORES.SEARCH_HISTORY);
    
    const searchData = {
      query: encryptedQuery,
      query_encrypted: true,
      query_length: search.query?.length || 0,
      module: search.module || 'chat',
      timestamp: new Date().toISOString(),
      resulted_in_document: search.resulted_in_document || false,
      document_id: search.document_id || null
    };

    const request = store.add(searchData);
    
    request.onsuccess = () => resolve({ ...searchData, id: request.result, query: search.query });
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get search history (decrypted)
 * @param {number} limit - Max number of results
 */
export const getSearchHistory = async (limit = 50) => {
  const status = await getStorageStatus();
  if (!status.available) {
    return [];
  }

  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SEARCH_HISTORY], 'readonly');
    const store = transaction.objectStore(STORES.SEARCH_HISTORY);
    const index = store.index('timestamp');
    const request = index.openCursor(null, 'prev');
    
    const results = [];
    
    request.onsuccess = async (event) => {
      const cursor = event.target.result;
      if (cursor && results.length < limit) {
        const search = cursor.value;
        
        // Decrypt query if encrypted
        if (search.query_encrypted && search.query) {
          search.query = await decryptContent(search.query);
        }
        
        results.push(search);
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all search history
 */
export const clearSearchHistory = async () => {
  const status = await getStorageStatus();
  if (!status.available) {
    return true;
  }

  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SEARCH_HISTORY], 'readwrite');
    const store = transaction.objectStore(STORES.SEARCH_HISTORY);
    const request = store.clear();
    
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete single search entry
 * @param {number} id - Search entry ID
 */
export const deleteSearchEntry = async (id) => {
  const status = await getStorageStatus();
  if (!status.available) {
    return true;
  }

  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SEARCH_HISTORY], 'readwrite');
    const store = transaction.objectStore(STORES.SEARCH_HISTORY);
    const request = store.delete(id);
    
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

// ═══════════════════════════════════════════════════════════════
// SETTINGS OPERATIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Save setting locally
 */
export const saveSetting = async (key, value) => {
  const status = await getStorageStatus();
  if (!status.available) {
    return false;
  }

  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SETTINGS], 'readwrite');
    const store = transaction.objectStore(STORES.SETTINGS);
    const request = store.put({ key, value, updated_at: new Date().toISOString() });
    
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get setting
 */
export const getSetting = async (key) => {
  const status = await getStorageStatus();
  if (!status.available) {
    return null;
  }

  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SETTINGS], 'readonly');
    const store = transaction.objectStore(STORES.SETTINGS);
    const request = store.get(key);
    
    request.onsuccess = () => resolve(request.result?.value || null);
    request.onerror = () => reject(request.error);
  });
};

// ═══════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Generate simple hash for content verification
 */
const generateHash = (content) => {
  if (!content) return null;
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(16);
};

/**
 * Get storage statistics
 */
export const getStorageStats = async () => {
  const status = await getStorageStatus();
  if (!status.available) {
    return {
      available: false,
      documentCount: 0,
      searchCount: 0,
      estimatedSize: 0,
      estimatedSizeFormatted: '0 Bytes'
    };
  }

  const docs = await getAllLocalDocuments();
  const searches = await getSearchHistory(1000);
  
  let totalSize = 0;
  docs.forEach(doc => {
    totalSize += new Blob([doc.content || '']).size;
  });
  
  return {
    available: true,
    documentCount: docs.length,
    searchCount: searches.length,
    estimatedSize: totalSize,
    estimatedSizeFormatted: formatBytes(totalSize),
    encrypted: true,
    dbVersion: DB_VERSION
  };
};

/**
 * Format bytes to human readable
 */
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Clear all local data (for privacy/reset) - requires confirmation
 */
export const clearAllLocalData = async (confirmed = false) => {
  if (!confirmed) {
    throw new Error('CLEAR_NOT_CONFIRMED');
  }

  const status = await getStorageStatus();
  if (!status.available) {
    return true;
  }

  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      [STORES.DOCUMENTS, STORES.SEARCH_HISTORY, STORES.SETTINGS],
      'readwrite'
    );
    
    transaction.objectStore(STORES.DOCUMENTS).clear();
    transaction.objectStore(STORES.SEARCH_HISTORY).clear();
    transaction.objectStore(STORES.SETTINGS).clear();
    
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Export all local data (for backup)
 */
export const exportLocalData = async () => {
  const status = await getStorageStatus();
  if (!status.available) {
    return null;
  }

  const docs = await getAllLocalDocuments();
  const searches = await getSearchHistory(10000);
  
  // Get full content for each document (decrypted)
  const fullDocs = await Promise.all(
    docs.map(async (doc) => {
      const fullDoc = await getDocumentLocally(doc.id);
      return fullDoc;
    })
  );
  
  return {
    version: '1.1.1',
    exported_at: new Date().toISOString(),
    documents: fullDocs,
    search_history: searches
  };
};

/**
 * Import documents from backup file
 */
export const importLocalData = async (data, confirmed = false) => {
  if (!confirmed) {
    throw new Error('IMPORT_NOT_CONFIRMED');
  }

  const status = await getStorageStatus();
  if (!status.available) {
    throw new Error('STORAGE_UNAVAILABLE');
  }

  let imported = { documents: 0, searches: 0 };

  // Import documents
  if (data.documents && Array.isArray(data.documents)) {
    for (const doc of data.documents) {
      if (doc.id && doc.content) {
        await saveDocumentLocally(doc.id, doc);
        imported.documents++;
      }
    }
  }

  // Import searches
  if (data.search_history && Array.isArray(data.search_history)) {
    for (const search of data.search_history) {
      if (search.query) {
        await saveSearchLocally(search);
        imported.searches++;
      }
    }
  }

  return imported;
};

/**
 * Initialize the service (call on app start)
 */
export const initLocalStorage = async () => {
  try {
    const status = await getStorageStatus();
    
    if (!status.available) {
      console.warn('RettVerk Local Storage: NOT AVAILABLE -', status.message);
      return { available: false, message: status.message };
    }
    
    await initDB();
    // console.log('RettVerk Local Storage initialized (Privacy-First v1.1 mode, encrypted)');
    return { available: true, message: null };
  } catch (error) {
    console.error('Failed to initialize local storage:', error);
    return { 
      available: false, 
      message: 'RettVerk kan ikke lagre dokumenter og søk lokalt på denne enheten.'
    };
  }
};

export default {
  // Status
  checkStorageAvailability,
  getStorageStatus,
  LOCAL_STATUS,
  
  // Documents
  saveDocumentLocally,
  getDocumentLocally,
  getAllLocalDocuments,
  updateDocumentLocally,
  deleteDocumentLocally,
  documentExistsLocally,
  getDocumentLocalStatus,
  markDocumentAsAvailable,
  
  // Search
  saveSearchLocally,
  getSearchHistory,
  clearSearchHistory,
  deleteSearchEntry,
  
  // Settings
  saveSetting,
  getSetting,
  
  // Utilities
  getStorageStats,
  clearAllLocalData,
  exportLocalData,
  importLocalData,
  initLocalStorage
};
